import React, { useEffect, useState } from "react";
import HappySchoolDataTable, { TABLE_CONFIG } from "./HappySchoolDataTable";
import HappySchoolFilteredView from "./HappySchoolFilteredView";
import { getCustomTable, saveCustomTable } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Tree theo Năm học dùng chung. Mỗi năm mở ra các danh sách con (subLists),
// mỗi danh sách là 1 bảng độc lập với tableKey riêng theo năm: `${keyPrefix}_${listKey}_${năm}`.
//
// Props:
//   title, icon   — tiêu đề trang
//   subLists      — mảng tableKey các danh sách con (lấy cột/tiêu đề từ TABLE_CONFIG)
//   yearsKey      — key bảng lưu danh mục năm học (rows: [{id, label}])
//   keyPrefix     — tiền tố tableKey cho dữ liệu theo năm

const slugYear = (label) =>
  String(label || "").trim().replace(/[^a-zA-Z0-9-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 20);

const HappySchoolYearTree = ({ title, icon, subLists = [], yearsKey, keyPrefix }) => {
  const schoolId = getActiveSchoolId();
  const subTableKey = (yearLabel, listKey) => `${keyPrefix}_${listKey}_${slugYear(yearLabel)}`;

  const [years, setYears] = useState([]);
  const [expanded, setExpanded] = useState({}); // {yearId: bool}
  const [selected, setSelected] = useState(null); // {yearLabel, listKey}
  const [newYear, setNewYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId) { setLoading(false); return; }
    setLoading(true);
    setSelected(null);
    getCustomTable(schoolId, yearsKey)
      .then((data) => {
        const rows = (data && data.rows) || [];
        setYears(rows);
        setExpanded(rows.length ? { [rows[0].id]: true } : {});
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, yearsKey]);

  const persist = (rows) => {
    setYears(rows);
    setSaving(true);
    saveCustomTable(schoolId, yearsKey, {
      columns: [{ key: "label", label: "Năm học", width: 140 }],
      rows,
    })
      .catch((e) => setError(e.message))
      .finally(() => setSaving(false));
  };

  const addYear = () => {
    const label = newYear.trim();
    if (!label) return;
    if (years.some((y) => y.label === label)) { setNewYear(""); return; }
    const row = { id: `y_${Date.now()}`, label };
    persist([...years, row]);
    setExpanded((e) => ({ ...e, [row.id]: true }));
    setNewYear("");
  };

  const removeYear = (id) => {
    if (!window.confirm("Xoá năm học này khỏi danh mục? (Dữ liệu bảng đã nhập vẫn còn, chỉ ẩn khỏi cây)")) return;
    persist(years.filter((y) => y.id !== id));
    setSelected((s) => (s && years.find((y) => y.id === id && y.label === s.yearLabel) ? null : s));
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const rawSelectedCfg = selected ? TABLE_CONFIG[selected.listKey] : null;
  const selectedCfg = rawSelectedCfg
    ? { ...rawSelectedCfg, title: `${rawSelectedCfg.title} — ${selected.yearLabel}` }
    : null;

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: "0 0 4px" }}>{icon} {title}</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Chọn năm học ở cây bên trái → mở các danh sách con. Mỗi năm có dữ liệu riêng.
      </p>
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={treeBox}>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <input
              value={newYear}
              onChange={(e) => setNewYear(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addYear()}
              placeholder="VD: 2025-2026"
              style={inputStyle}
            />
            <button onClick={addYear} style={addBtn}>+ Năm</button>
          </div>
          {saving && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Đang lưu…</div>}
          {loading ? (
            <div style={{ color: "#6b7280", fontSize: 13 }}>Đang tải…</div>
          ) : years.length === 0 ? (
            <div style={{ color: "#6b7280", fontSize: 13 }}>Chưa có năm học. Thêm năm ở trên.</div>
          ) : (
            years.map((y) => (
              <div key={y.id}>
                <div style={yearRow}>
                  <span onClick={() => toggle(y.id)} style={{ cursor: "pointer", flex: 1, fontWeight: 600 }}>
                    {expanded[y.id] ? "▾" : "▸"} 📅 {y.label}
                  </span>
                  <span onClick={() => removeYear(y.id)} title="Xoá năm" style={delX}>✕</span>
                </div>
                {expanded[y.id] &&
                  subLists.map((lk) => {
                    const cfg = TABLE_CONFIG[lk];
                    if (!cfg) return null;
                    const isSel = selected && selected.yearLabel === y.label && selected.listKey === lk;
                    return (
                      <div
                        key={lk}
                        onClick={() => setSelected({ yearLabel: y.label, listKey: lk })}
                        style={{ ...childRow, ...(isSel ? childRowSel : {}) }}
                      >
                        {cfg.icon} {cfg.title}
                      </div>
                    );
                  })}
              </div>
            ))
          )}
        </div>

        <div style={{ flex: 1, minWidth: 320 }}>
          {selected && rawSelectedCfg.filterOf ? (
            <HappySchoolFilteredView
              key={subTableKey(selected.yearLabel, rawSelectedCfg.filterOf) + "_" + selected.listKey}
              schoolId={schoolId}
              sourceTableKey={subTableKey(selected.yearLabel, rawSelectedCfg.filterOf)}
              title={selectedCfg.title}
              icon={rawSelectedCfg.icon}
              predicate={rawSelectedCfg.predicate}
            />
          ) : selected ? (
            <HappySchoolDataTable
              key={subTableKey(selected.yearLabel, selected.listKey)}
              tableKey={subTableKey(selected.yearLabel, selected.listKey)}
              config={selectedCfg}
            />
          ) : (
            <div style={{ color: "#6b7280", padding: 24 }}>
              ← Chọn một danh sách trong cây để xem/nhập dữ liệu.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const treeBox = { width: 300, flexShrink: 0, border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff" };
const inputStyle = { flex: 1, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, minWidth: 0 };
const addBtn = { padding: "6px 10px", border: "none", borderRadius: 6, background: "#0ea5a3", color: "#fff", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
const yearRow = { display: "flex", alignItems: "center", gap: 6, padding: "6px 4px", borderRadius: 6, textAlign: "left" };
const delX = { cursor: "pointer", color: "#dc2626", fontSize: 12, padding: "0 4px" };
const childRow = { cursor: "pointer", padding: "6px 8px 6px 28px", fontSize: 13, borderRadius: 6, color: "#374151", textAlign: "left" };
const childRowSel = { background: "#f0fdfa", color: "#0f766e", fontWeight: 600 };

export default HappySchoolYearTree;
